/**
 * Neue Institution anlegen – eigenständige Tab-Seite
 * Ersetzt den früheren modalen NeueInstitutionDialog
 * Side-by-Side: Formular links, Dokumentvorschau rechts (wenn vorhanden)
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Building2, Check, Loader2, Search, FileText, Copy, X, ZoomIn, ZoomOut
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { betreuungAPI } from '@/api/betreuungAPI';
import { useTabs } from '@/contexts/TabContext';
import { emitEntityCreated, ENTITY_TYPES } from '@/lib/tabEvents';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function NeueInstitutionPage() {
  const { activeTab, closeTab } = useTabs();
  const params = activeTab?.params || {};

  const [form, setForm] = useState({
    name: '', typ: '', strasse: '', plz: '', ort: '',
    telefon: '', email: '', webseite: '', notiz: ''
  });
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);
  const [existingInstitutions, setExistingInstitutions] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);

  // Dokumentvorschau
  const [previewZoom, setPreviewZoom] = useState(100);
  const [previewTab, setPreviewTab] = useState('dokument');

  const hasPreview = !!(params.dokumentVorschauUrl || params.ocrText);

  // Institutionen und Initialdaten laden
  useEffect(() => {
    betreuungAPI.listJuristischePersonen().then(res => {
      setExistingInstitutions(Array.isArray(res) ? res : []);
    }).catch(() => {});

    if (params.name) setForm(prev => ({ ...prev, name: params.name }));
    if (params.typ) setForm(prev => ({ ...prev, typ: params.typ }));
    if (params.strasse) setForm(prev => ({ ...prev, strasse: params.strasse }));
    if (params.plz) setForm(prev => ({ ...prev, plz: params.plz }));
    if (params.ort) setForm(prev => ({ ...prev, ort: params.ort }));
    if (params.telefon) setForm(prev => ({ ...prev, telefon: params.telefon }));
    if (params.email) setForm(prev => ({ ...prev, email: params.email }));
    if (params.webseite) setForm(prev => ({ ...prev, webseite: params.webseite }));

    setPreviewTab(params.dokumentVorschauUrl ? 'dokument' : 'ocr');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNameChange = (value) => {
    setForm(p => ({ ...p, name: value }));
    if (value.length >= 2) {
      const matches = existingInstitutions.filter(inst =>
        inst.name?.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 5);
      setSearchResults(matches);
      setShowSearch(matches.length > 0);
    } else {
      setSearchResults([]);
      setShowSearch(false);
    }
  };

  const handleSelectExisting = (inst) => {
    emitEntityCreated(ENTITY_TYPES.INSTITUTION, inst);
    toast.success(`Bestehende Institution "${inst.name}" ausgewählt`);
    closeTab(activeTab.id);
  };

  const handleKiRecherche = async () => {
    if (!form.name.trim()) { toast.error('Bitte zuerst einen Namen eingeben'); return; }
    setSearching(true);
    try {
      const token = localStorage.getItem('olympus_token');
      const res = await fetch(`${BACKEND_URL}/api/wiki/recherche-institution`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name: form.name, typ: form.typ || '' })
      });
      const data = await res.json();
      if (data.ergebnis) {
        const e = data.ergebnis;
        setForm(prev => ({
          ...prev,
          strasse: e.strasse || e.adresse || prev.strasse,
          plz: e.plz || prev.plz,
          ort: e.ort || e.stadt || prev.ort,
          telefon: e.telefon || prev.telefon,
          email: e.email || prev.email,
          webseite: e.webseite || e.website || prev.webseite,
          typ: e.typ || prev.typ,
        }));
        toast.success('Kontaktdaten gefunden und eingetragen!');
      } else {
        toast.info('Keine Kontaktdaten gefunden. Bitte manuell eingeben.');
      }
    } catch {
      toast.error('KI-Recherche fehlgeschlagen');
    } finally {
      setSearching(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name ist erforderlich'); return; }
    setSaving(true);
    try {
      const result = await betreuungAPI.createJuristischePerson({
        name: form.name,
        typ: form.typ || 'sonstiges',
        strasse: form.strasse || null,
        plz: form.plz || null,
        ort: form.ort || null,
        telefon: form.telefon || null,
        email: form.email || null,
        webseite: form.webseite || null,
        notizen: form.notiz || null
      });
      toast.success(`Institution "${form.name}" angelegt`);
      emitEntityCreated(ENTITY_TYPES.INSTITUTION, result);
      closeTab(activeTab.id);
    } catch {
      toast.error('Fehler beim Anlegen');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Text kopiert');
  };

  const handleClose = useCallback(() => {
    closeTab(activeTab.id);
  }, [closeTab, activeTab?.id]);

  return (
    <div className={`${hasPreview ? 'max-w-5xl' : 'max-w-xl'} mx-auto`} data-testid="neue-institution-page">
      <div className={`flex gap-4 ${hasPreview ? 'flex-row' : 'flex-col'}`}>

        {/* LINKS: Formular */}
        <Card className={hasPreview ? 'w-1/2' : 'w-full'}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building2 className="w-5 h-5 text-blue-600" />
                Neue Institution anlegen
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={handleClose} data-testid="close-neue-institution">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <CardDescription>Erfassen Sie die Daten oder nutzen Sie die KI-Recherche</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Name mit Suche und KI-Recherche */}
            <div className="relative">
              <Label className="text-xs">Name *</Label>
              <div className="flex gap-2 mt-0.5">
                <Input data-testid="institution-name-input" value={form.name}
                  onChange={e => handleNameChange(e.target.value)}
                  placeholder="z.B. Jobcenter München" className="flex-1 h-9 text-sm" />
                <Button type="button" variant="outline" size="sm"
                  data-testid="ki-recherche-btn"
                  onClick={handleKiRecherche} disabled={searching || !form.name.trim()}
                  className="h-9 text-xs whitespace-nowrap text-purple-700 border-purple-300 hover:bg-purple-50">
                  {searching
                    ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Suche...</>
                    : <><Search className="w-3 h-3 mr-1" /> KI-Recherche</>}
                </Button>
              </div>
              {showSearch && searchResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-blue-200 rounded-lg shadow-lg max-h-36 overflow-y-auto">
                  <div className="px-3 py-1 text-[10px] text-blue-600 font-medium bg-blue-50 border-b">Bereits vorhanden:</div>
                  {searchResults.map(inst => (
                    <button key={inst.id}
                      className="w-full text-left px-3 py-1.5 hover:bg-blue-50 border-b border-slate-100 last:border-0 text-sm"
                      onClick={() => handleSelectExisting(inst)}>
                      <span className="font-medium">{inst.name}</span>
                      {inst.ort && <span className="text-xs text-slate-500 ml-2">{inst.ort}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Typ */}
            <div>
              <Label className="text-xs">Typ</Label>
              <Select value={form.typ || 'none'} onValueChange={v => setForm(p => ({ ...p, typ: v === 'none' ? '' : v }))}>
                <SelectTrigger className="h-9 text-sm mt-0.5"><SelectValue placeholder="Typ auswählen..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Kein Typ --</SelectItem>
                  <SelectItem value="behoerde">Behörde</SelectItem>
                  <SelectItem value="gericht">Gericht</SelectItem>
                  <SelectItem value="bank">Bank</SelectItem>
                  <SelectItem value="versicherung">Versicherung</SelectItem>
                  <SelectItem value="krankenhaus">Krankenhaus</SelectItem>
                  <SelectItem value="pflegeheim">Pflegeheim</SelectItem>
                  <SelectItem value="arztpraxis">Arztpraxis</SelectItem>
                  <SelectItem value="vermieter">Vermieter</SelectItem>
                  <SelectItem value="versorger">Versorger</SelectItem>
                  <SelectItem value="sonstiges">Sonstiges</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Adresse */}
            <div>
              <Label className="text-xs">Adresse</Label>
              <Input className="h-9 text-sm mt-0.5" value={form.strasse}
                onChange={e => setForm(p => ({ ...p, strasse: e.target.value }))} placeholder="Strasse und Hausnummer" />
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                <Input className="h-9 text-sm" value={form.plz}
                  onChange={e => setForm(p => ({ ...p, plz: e.target.value }))} placeholder="PLZ" />
                <Input className="col-span-2 h-9 text-sm" value={form.ort}
                  onChange={e => setForm(p => ({ ...p, ort: e.target.value }))} placeholder="Ort" />
              </div>
            </div>

            {/* Kontakt */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Telefon</Label>
                <Input className="h-9 text-sm mt-0.5" value={form.telefon}
                  onChange={e => setForm(p => ({ ...p, telefon: e.target.value }))} placeholder="089 123456" />
              </div>
              <div>
                <Label className="text-xs">E-Mail</Label>
                <Input className="h-9 text-sm mt-0.5" type="email" value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="info@beispiel.de" />
              </div>
            </div>

            {/* Webseite + Notiz */}
            <div>
              <Label className="text-xs">Webseite</Label>
              <Input className="h-9 text-sm mt-0.5" value={form.webseite}
                onChange={e => setForm(p => ({ ...p, webseite: e.target.value }))} placeholder="https://www.beispiel.de" />
            </div>
            <div>
              <Label className="text-xs">Notiz</Label>
              <Textarea className="text-sm mt-0.5" value={form.notiz}
                onChange={e => setForm(p => ({ ...p, notiz: e.target.value }))}
                placeholder="Weitere Informationen..." rows={2} />
            </div>

            {/* Aktionen */}
            <div className="flex justify-end gap-2 pt-3 border-t">
              <Button variant="outline" size="sm" onClick={handleClose}>Abbrechen</Button>
              <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-neue-institution">
                {saving
                  ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Speichern...</>
                  : <><Check className="w-3 h-3 mr-1" /> Institution anlegen</>}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* RECHTS: Dokumentvorschau */}
        {hasPreview && (
          <Card className="w-1/2 flex flex-col overflow-hidden" data-testid="institution-doc-preview">
            {/* Preview Header */}
            <div className="px-4 py-2 bg-slate-50 border-b flex items-center justify-between shrink-0">
              <div className="flex items-center gap-1">
                {params.dokumentVorschauUrl && (
                  <button className={`px-2.5 py-1 text-xs rounded-md font-medium transition-colors ${
                    previewTab === 'dokument' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'
                  }`} onClick={() => setPreviewTab('dokument')}>Dokument</button>
                )}
                {params.ocrText && (
                  <button className={`px-2.5 py-1 text-xs rounded-md font-medium transition-colors ${
                    previewTab === 'ocr' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'
                  }`} onClick={() => setPreviewTab('ocr')}>OCR-Text</button>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7"
                  onClick={() => setPreviewZoom(z => Math.max(50, z - 25))}>
                  <ZoomOut className="w-3.5 h-3.5" />
                </Button>
                <span className="text-[10px] text-slate-500 w-9 text-center">{previewZoom}%</span>
                <Button variant="ghost" size="icon" className="h-7 w-7"
                  onClick={() => setPreviewZoom(z => Math.min(300, z + 25))}>
                  <ZoomIn className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>

            {/* Dateiname */}
            <div className="px-4 py-1.5 text-[11px] text-slate-500 border-b bg-slate-50/50 flex items-center gap-1">
              <FileText className="w-3 h-3 shrink-0" />
              <span className="truncate">{params.dokumentName || 'Dokument'}</span>
            </div>

            {/* Inhalt */}
            <div className="flex-1 overflow-auto" style={{ maxHeight: 'calc(100vh - 260px)' }}>
              {previewTab === 'dokument' && params.dokumentVorschauUrl && (
                <div style={{ transform: `scale(${previewZoom / 100})`, transformOrigin: 'top left', width: `${10000 / previewZoom}%` }}>
                  {params.dokumentVorschauUrl.match(/\.(jpg|jpeg|png|gif|webp)/i)
                    ? <img src={params.dokumentVorschauUrl} alt="Dokument" className="w-full" />
                    : <iframe src={params.dokumentVorschauUrl} title="Dokument-Vorschau"
                        className="w-full border-0" style={{ height: `${Math.max(600, 600 * (previewZoom / 100))}px` }} />}
                </div>
              )}
              {previewTab === 'ocr' && params.ocrText && (
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] text-slate-400 font-medium">OCR-erkannter Text</span>
                    <button className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      onClick={() => handleCopyText(params.ocrText)} title="Gesamten Text kopieren">
                      <Copy className="w-3 h-3" /> Alles kopieren
                    </button>
                  </div>
                  <div className="bg-white rounded-lg border p-3 text-xs leading-relaxed whitespace-pre-wrap select-text cursor-text font-mono"
                    style={{ fontSize: `${Math.max(10, 12 * (previewZoom / 100))}px` }}>
                    {params.ocrText}
                  </div>
                </div>
              )}
              {previewTab === 'ocr' && !params.ocrText && (
                <div className="flex items-center justify-center h-40 text-slate-400 text-sm p-6 text-center">
                  Kein OCR-Text verfügbar.
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
